import Row from "react-bootstrap/Row";
import Form from "react-bootstrap/Form";
import Col from "react-bootstrap/Col";
import {
  FieldError,
  FieldErrors,
  FieldPath,
  FieldPathByValue,
  get,
  useFormContext
} from "react-hook-form";
import {ReactNode, useState} from "react";
import {loadPhoneNumberCountryCodes, PhoneNumberEntry} from "../../../util/PhoneUtils";
import {Button} from "react-bootstrap";
import Dropdown from "./Dropdown";

/**
 * Props for this component
 */
interface PhoneNumberProps<T extends Record<string, any>> {
  label: string;
  helpText?: ReactNode;
  errors: FieldErrors<T> | undefined;
  fieldId: FieldPathByValue<T, PhoneNumberEntry>;
  disabled?: boolean;
  mandatory?: boolean;
  minLength?: number;
  maxLength?: number;
  validateCode?: (v: string) => true | string;
  validateNumber?: (v: string) => true | string;
  deletePhoneTrigger?: () => void;
}

/**
 * This is a component that will add a input for the user to add a phone number. It was designed to be used as part of a
 * form panel. You may pass the following props:
 *
 * @param label The text to show before the input. (By default this will show to the left of the input)
 * @param helpText Subtext that shows on top of the question
 * @param errors The full list of errors from React useForm. <b>It will only process the errors with the same field ID you pass.</b>
 * @param fieldId The ID of the field as defined in the form interface
 * @param disabled <i>Optional</i> A boolean to mark the field as disabled
 * @param mandatory <i>Optional</i> A boolean to mark the field as mandatory
 * @param minLength <i>Optional</i> The minimum number of chars this field can take
 * @param maxLength <i>Optional</i> The maximum number of chars this field can take
 * @param validateCode <i>Optional</i> A custom function to validate the phone code
 * @param validateNumber <i>Optional</i> A custom function to validate the phone number
 * @param deletePhoneTrigger <i>Optional</i> It is common to have up to 3 phone number in AMS (2 optional ones). This will allow us
 * to delete optional ones
 */
export default function PhoneNumber<T extends Record<string, any>>({
                                                                     label,
                                                                     helpText,
                                                                     errors,
                                                                     fieldId,
                                                                     disabled = false,
                                                                     mandatory = false,
                                                                     minLength,
                                                                     maxLength,
                                                                     validateCode,
                                                                     validateNumber,
                                                                     deletePhoneTrigger
                                                                   }: PhoneNumberProps<T>) {
  const {register} = useFormContext<T>();
  const [countryCodes, setCountryCodes] = useState<{
    code: string;
    countryISOCode: string;
    countryName: string
  }[]>([]);

  // Work out what the paths for the specific form elements are
  const codePath = `${fieldId}.countryCode` as FieldPath<T>;
  const numberPath = `${fieldId}.number` as FieldPath<T>;

  // Find errors
  const codeError = get(errors, codePath) as FieldError | undefined;
  const numberError = get(errors, numberPath) as FieldError | undefined;

  const loadCountryCodes = () => {
    setCountryCodes(loadPhoneNumberCountryCodes());
  }

  // This is to be used when generating data-test-ids
  const fieldIdForDataTest = label.toLowerCase().replace(" ", "-");

  return <Form.Group controlId={`formHorizontal${fieldId}`}>
    {helpText && <Row>
      <Col sm={2}></Col>
      <Col sm={10} className={"question-help-text"}>
        {helpText}
      </Col>
    </Row>}
    <Dropdown<T>
      label={label}
      content={countryCodes.map(c => ({
        key: c.code,
        label: `${c.countryName} (${c.code})`
      }))}
      lazyLoadTrigger={loadCountryCodes}
      error={codeError}
      fieldId={codePath}
      defaultValue={{
        key: "+44",
        label: "United Kingdom (+44)"
      }}
      mandatory={mandatory}
      disabled={disabled}
      sideAction={<>
        <Col>
          <Form.Control
            type="text"
            placeholder={label}
            disabled={disabled}
            {...register(numberPath, {
              // Normalize value
              setValueAs: (v) => {
                const trimmed = v.trim();
                return (mandatory) ?
                  trimmed :
                  trimmed === "" ? undefined : trimmed;
              },
              required: mandatory ? `Phone number is required` : false,
              minLength: minLength ? {
                value: minLength,
                message: `Phone number must be at least ${minLength} characters`
              } : undefined,
              maxLength: maxLength ? {
                value: maxLength,
                message: `Phone number must not be longer than ${maxLength} characters`
              } : undefined,
              validate: validateNumber ?
                (v: string) => validateNumber(v) :
                undefined
            })}
            isInvalid={!!numberError}
            data-testid={`${fieldIdForDataTest}-number`}
          />
          <Form.Control.Feedback type="invalid" data-testid={`${fieldIdForDataTest}-number-error`}>
            {numberError?.message}
          </Form.Control.Feedback>
        </Col>
        {/*Section to delete entry*/}
        {deletePhoneTrigger && !mandatory && <Col sm={"auto"}>
          <Button variant="danger" onClick={deletePhoneTrigger}><i
              className="bi bi-trash3"></i></Button>
        </Col>}
      </>}
    />
  </Form.Group>;
}